import Head from "next/head";

const SITE_NAME = "KMCP Accounting";
const DEFAULT_BASE_URL = "https://www.kmcpaccounting.com";
const DEFAULT_DESCRIPTION =
  "KMCP Accounting ให้บริการทำบัญชี ยื่นภาษี ปิดบัญชีประจำปี จัดทำเงินเดือน และจดทะเบียนธุรกิจในเชียงใหม่";
const DEFAULT_IMAGE = "/images/hero-cover.jpg";

const stripTrailingSlash = (value: string) => value.replace(/\/$/, "");

const stripQueryAndHash = (value: string) => value.split("#")[0]?.split("?")[0] ?? "";

const ensureLeadingSlash = (value: string) => (value.startsWith("/") ? value : `/${value}`);

const buildCanonicalUrl = (raw: string, base: string) => {
  if (!raw) {
    return base;
  }
  if (raw.startsWith("http")) {
    try {
      const url = new URL(raw);
      url.hash = "";
      url.search = "";
      return url.toString();
    } catch (error) {
      return raw;
    }
  }

  const sanitizedPath = stripQueryAndHash(raw) || "/";
  const normalizedPath = sanitizedPath === "/" ? "/" : ensureLeadingSlash(sanitizedPath);
  return `${base}${normalizedPath}`;
};

const buildAssetUrl = (raw: string, base: string) => {
  if (!raw) {
    return `${base}/`;
  }
  if (raw.startsWith("http")) {
    return raw;
  }
  return `${base}${ensureLeadingSlash(raw)}`;
};

export type SeoProps = {
  title?: string;
  description?: string;
  /** Absolute URL. If omitted, will fall back to base URL + current path. */
  canonicalUrl?: string;
  /** Relative path that will be appended to the base URL when canonicalUrl is not provided. */
  path?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  type?: "website" | "article";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl,
  path,
  image = DEFAULT_IMAGE,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 630,
  type = "website",
  noindex = false,
  jsonLd
}: SeoProps) {
  const baseUrl = stripTrailingSlash(process.env.NEXT_PUBLIC_SITE_URL ?? DEFAULT_BASE_URL);
  const rawPath = canonicalUrl ?? path ?? "/";

  const canonical = buildCanonicalUrl(rawPath, baseUrl);

  const finalTitle = title
    ? Array.from(title).length > 48
      ? title
      : `${title} | ${SITE_NAME}`
    : SITE_NAME;
  const imageUrl = buildAssetUrl(image, baseUrl);

  return (
    <Head>
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
      />

      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="th_TH" />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={imageWidth.toString()} />
      <meta property="og:image:height" content={imageHeight.toString()} />
      {imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      <link rel="canonical" href={canonical} key="canonical" />
      {jsonLd ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ) : null}
    </Head>
  );
}
